import { useEffect, useRef, useState } from 'react';
import type { ChatMessage, StreamEvent } from '@hypevault/shared-types';
import { TWITCH_BADGE_URL, BADGE_LABEL } from '../chatBadges';

/**
 * Multi-Chat — v2 port of v1's multi-chat.ejs + MultiChatSettings.
 * Unified chat overlay showing messages from Twitch + YouTube + Kick,
 * with inline YouTube-style alert rows for follows / subs / tips /
 * cheers / raids / SuperChats interleaved into the feed by timestamp.
 *
 * Config (all optional):
 *   maxMessages: number                       (default 25)
 *   accentColor: string                       (default '#a78bfa')
 *   bgStyle: 'semi' | 'solid' | 'transparent' (default 'semi')
 *   fontSize: number                          (default 14)
 *   showBadges: boolean                       (default true)
 *   showPlatformIcon: boolean                 (default true)
 *   showTimestamps: boolean                   (default false)
 *   fadeOldMessages: boolean                  (default true)
 *   messageAnimation: 'slide' | 'fade'        (default 'slide')
 *   hideCommands: boolean                     (default true — drop messages starting with '!')
 *   showAlerts: boolean                       (default true — inline YT-style event rows)
 *   startFromTop: boolean                     (default false — true anchors chat from the top edge
 *                                              instead of the bottom)
 *   bottomPadding: number                     (default 32 — px gap from the visible bottom edge so
 *                                              messages don't hug it. Ignored when startFromTop=true)
 */

const PLATFORM_COLOR: Record<string, string> = {
  twitch: '#9146ff',
  youtube: '#ff0000',
  kick: '#53fc18',
  hypevault: '#f655a6',
};

/** YouTube SuperChat color scale, in cents. Highest tier the amount
 *  meets wins. The colors and thresholds match the YouTube product
 *  exactly so streamers reading along get the familiar visual ladder. */
function superChatTier(amountCents: number): {
  bg: string;
  bar: string;
  fg: string;
  label: string;
} {
  if (amountCents >= 10000) return { bg: '#E62117', bar: '#B71C1C', fg: '#FFFFFF', label: 'SUPER' };
  if (amountCents >= 5000) return { bg: '#E91E63', bar: '#AD1457', fg: '#FFFFFF', label: 'SUPER' };
  if (amountCents >= 2000) return { bg: '#F57C00', bar: '#E65100', fg: '#FFFFFF', label: 'SUPER' };
  if (amountCents >= 1000) return { bg: '#FFCA28', bar: '#FFA000', fg: '#000000', label: 'SUPER' };
  if (amountCents >= 500) return { bg: '#1DE9B6', bar: '#00BFA5', fg: '#000000', label: 'SUPER' };
  if (amountCents >= 200) return { bg: '#00E5FF', bar: '#00B8D4', fg: '#000000', label: 'SUPER' };
  return { bg: '#1E88E5', bar: '#1565C0', fg: '#FFFFFF', label: 'SUPER' };
}

function formatMoney(cents: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function backgroundFor(style: string) {
  if (style === 'solid')
    return {
      background: 'linear-gradient(135deg,#0a0818,#120e26)',
      border: '1px solid rgba(139,92,246,0.2)',
    };
  if (style === 'transparent') return { background: 'transparent', border: 'none' };
  return {
    background: 'linear-gradient(135deg, rgba(10,8,24,0.86), rgba(18,14,38,0.86))',
    border: '1px solid rgba(139,92,246,0.16)',
  };
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

type FeedItem =
  | { kind: 'msg'; id: string; ts: number; msg: ChatMessage }
  | { kind: 'evt'; id: string; ts: number; evt: StreamEvent };

export function MultiChatWidget({
  fields,
  latestChatMessage,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestChatMessage: ChatMessage | null;
  latestEvent?: StreamEvent | null;
}) {
  // Default size for this widget is 380×600 (catalog defaultSize). Tuned
  // for ~18 chat lines or ~10 alert rows at default fontSize. Streamers
  // who resize the OBS source larger can bump fontSize / maxMessages.
  const maxMessages = Number(fields.maxMessages ?? 22);
  const accent = String(fields.accentColor ?? '#a78bfa');
  const bgStyle = String(fields.bgStyle ?? 'semi');
  const fontSize = Number(fields.fontSize ?? 14);
  const showBadges = (fields.showBadges as boolean | undefined) ?? true;
  const showPlatformIcon = (fields.showPlatformIcon as boolean | undefined) ?? true;
  const showTimestamps = (fields.showTimestamps as boolean | undefined) ?? false;
  const fadeOldMessages = (fields.fadeOldMessages as boolean | undefined) ?? true;
  const animation = (fields.messageAnimation as 'slide' | 'fade' | undefined) ?? 'slide';
  const hideCommands = (fields.hideCommands as boolean | undefined) ?? true;
  const showAlerts = (fields.showAlerts as boolean | undefined) ?? true;
  const startFromTop = (fields.startFromTop as boolean | undefined) ?? false;
  const bottomPadding = Number(fields.bottomPadding ?? 16);

  const [items, setItems] = useState<FeedItem[]>([]);
  const lastMsgId = useRef<string | null>(null);
  const lastEvtId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestChatMessage || latestChatMessage.id === lastMsgId.current) return;
    lastMsgId.current = latestChatMessage.id;
    if (hideCommands && latestChatMessage.command) return;
    setItems((prev) =>
      [
        ...prev,
        {
          kind: 'msg',
          id: latestChatMessage.id,
          ts: latestChatMessage.timestamp,
          msg: latestChatMessage,
        } as FeedItem,
      ].slice(-maxMessages),
    );
  }, [latestChatMessage, maxMessages, hideCommands]);

  useEffect(() => {
    if (!showAlerts || !latestEvent || latestEvent.id === lastEvtId.current) return;
    // Only event types that make sense inline. Card redemptions / channel
    // point redemptions / alert purchases have their own dedicated widgets
    // and would clutter the chat.
    const inlineTypes: ReadonlySet<StreamEvent['type']> = new Set([
      'follow',
      'sub',
      'tip',
      'tip_received',
      'cheer',
      'raid',
      'superChat',
      'superSticker',
    ]);
    if (!inlineTypes.has(latestEvent.type)) return;
    lastEvtId.current = latestEvent.id;
    setItems((prev) =>
      [
        ...prev,
        {
          kind: 'evt',
          id: latestEvent.id,
          ts: latestEvent.timestamp,
          evt: latestEvent,
        } as FeedItem,
      ].slice(-maxMessages),
    );
  }, [latestEvent, maxMessages, showAlerts]);

  const bg = backgroundFor(bgStyle);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: "'Inter', system-ui, sans-serif",
        color: 'white',
        borderRadius: 10,
        // Bottom padding lifts chat off the visible bottom edge so the newest
        // line isn't flush against the OBS source frame. When the chat is
        // top-anchored, the lift would just be wasted blank space, so we
        // collapse it to the default 12px.
        padding: startFromTop ? 10 : `10px 10px ${Math.max(10, bottomPadding)}px 10px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: startFromTop ? 'flex-start' : 'flex-end',
        gap: 4,
        ...bg,
      }}
    >
      {items.map((it, i) => {
        const isOld = fadeOldMessages && i < items.length - 6;
        const animStyle =
          animation === 'fade'
            ? { animation: 'mc-fade 0.3s ease-out both' }
            : { animation: 'mc-slide 0.3s cubic-bezier(0.16,1,0.3,1) both' };

        if (it.kind === 'evt') {
          return (
            <EventRow
              key={it.id}
              event={it.evt}
              fontSize={fontSize}
              showTimestamps={showTimestamps}
              isOld={isOld}
              animStyle={animStyle}
              accent={accent}
            />
          );
        }

        const m = it.msg;
        const platformColor = PLATFORM_COLOR[m.platform] ?? accent;
        return (
          <div
            key={m.id}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 5,
              fontSize,
              lineHeight: 1.3,
              opacity: isOld ? 0.55 : 1,
              transition: 'opacity 400ms ease',
              ...animStyle,
            }}
          >
            {showPlatformIcon && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: platformColor,
                  flexShrink: 0,
                  alignSelf: 'center',
                }}
                title={m.platform}
              />
            )}
            {showBadges && m.badges.length > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  alignSelf: 'center',
                  gap: 3,
                  flexShrink: 0,
                }}
              >
                {m.badges.map((b) => (
                  <img
                    key={b}
                    src={TWITCH_BADGE_URL[b]}
                    alt={BADGE_LABEL[b]}
                    title={BADGE_LABEL[b]}
                    width={fontSize}
                    height={fontSize}
                    style={{ display: 'block', borderRadius: 3 }}
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </span>
            )}
            <span
              style={{
                fontWeight: 700,
                color: platformColor,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {m.viewerName}:
            </span>
            <span style={{ wordBreak: 'break-word', minWidth: 0 }}>{m.message}</span>
            {showTimestamps && (
              <span
                style={{
                  fontSize: fontSize * 0.7,
                  color: 'rgba(255,255,255,0.35)',
                  marginLeft: 'auto',
                  flexShrink: 0,
                }}
              >
                {formatTime(m.timestamp)}
              </span>
            )}
          </div>
        );
      })}
      {items.length === 0 && (
        <div
          style={{
            opacity: 0.3,
            fontSize: fontSize * 0.9,
            textAlign: 'center',
            padding: 12,
          }}
        >
          Waiting for chat…
        </div>
      )}
      <style>{`
        @keyframes mc-slide {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes mc-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes mc-pop {
          from { opacity: 0; transform: scale(0.94); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function EventRow({
  event,
  fontSize,
  showTimestamps,
  isOld,
  animStyle,
  accent,
}: {
  event: StreamEvent;
  fontSize: number;
  showTimestamps: boolean;
  isOld: boolean;
  animStyle: React.CSSProperties;
  accent: string;
}) {
  // Compact sizing tuned for the widget's 380×600 default canvas.
  // Every paid-event row keeps a 1-line header at ~24px (fontSize 14) +
  // an optional wrapped message body so a SuperChat with text lands
  // around 50–60px instead of 80–100px.
  const labelSize = Math.max(10, Math.round(fontSize * 0.72));
  const bodyPad = '4px 8px';
  const headerPad = '4px 8px';

  const baseStyle: React.CSSProperties = {
    fontSize,
    lineHeight: 1.3,
    borderRadius: 5,
    opacity: isOld ? 0.7 : 1,
    transition: 'opacity 400ms ease',
    ...animStyle,
    animation: 'mc-pop 0.32s cubic-bezier(0.34,1.56,0.64,1) both',
  };

  const ts = showTimestamps ? (
    <span
      style={{
        fontSize: fontSize * 0.7,
        color: 'rgba(255,255,255,0.55)',
        marginLeft: 6,
        flexShrink: 0,
      }}
    >
      {formatTime(event.timestamp)}
    </span>
  ) : null;

  const HeaderBar = ({
    label,
    bg,
    fg,
    viewer,
  }: {
    label: string;
    bg: string;
    fg: string;
    viewer: string;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: headerPad,
        background: bg,
        color: fg,
        fontWeight: 800,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: labelSize,
          letterSpacing: 0.8,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight: 700,
          marginLeft: 'auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {viewer}
      </span>
      {ts}
    </div>
  );

  // SuperChat / SuperSticker — YouTube paid-message bar.
  if (event.type === 'superChat' || event.type === 'superSticker') {
    const tier = superChatTier(event.amountCents);
    const amount = formatMoney(event.amountCents, event.currency || 'USD');
    const isSticker = event.type === 'superSticker';
    return (
      <div
        style={{
          ...baseStyle,
          background: tier.bg,
          color: tier.fg,
          borderLeft: `3px solid ${tier.bar}`,
          boxShadow: `0 2px 10px ${tier.bg}44`,
          overflow: 'hidden',
        }}
      >
        <HeaderBar
          label={`${isSticker ? 'STICKER' : 'SUPER'} · ${amount}`}
          bg={tier.bar}
          fg={tier.fg}
          viewer={event.viewerName}
        />
        {!isSticker && 'message' in event && event.message && (
          <div style={{ padding: bodyPad, wordBreak: 'break-word', fontWeight: 500 }}>
            {event.message}
          </div>
        )}
        {isSticker && (
          <div style={{ padding: '6px 8px', fontSize: fontSize * 1.3, textAlign: 'center' }}>
            🎉
          </div>
        )}
      </div>
    );
  }

  // Tip — colored bar ranked by amount, same scale as SuperChat.
  if (event.type === 'tip' || event.type === 'tip_received') {
    const cents = event.type === 'tip_received' ? event.amountCents : event.coins;
    const tier = superChatTier(cents);
    const amount =
      event.type === 'tip_received'
        ? formatMoney(event.amountCents)
        : `${event.coins.toLocaleString()} coins`;
    return (
      <div
        style={{
          ...baseStyle,
          background: tier.bg,
          color: tier.fg,
          borderLeft: `3px solid ${tier.bar}`,
          boxShadow: `0 2px 10px ${tier.bg}44`,
          overflow: 'hidden',
        }}
      >
        <HeaderBar
          label={`💰 TIP · ${amount}`}
          bg={tier.bar}
          fg={tier.fg}
          viewer={event.viewerName}
        />
        {event.message && (
          <div style={{ padding: bodyPad, wordBreak: 'break-word', fontWeight: 500 }}>
            {event.message}
          </div>
        )}
      </div>
    );
  }

  // Cheer (Twitch bits) — paid, scaled by bits-as-cents.
  if (event.type === 'cheer') {
    const tier = superChatTier(event.bits);
    return (
      <div
        style={{
          ...baseStyle,
          background: tier.bg,
          color: tier.fg,
          borderLeft: `3px solid ${tier.bar}`,
          boxShadow: `0 2px 10px ${tier.bg}44`,
          overflow: 'hidden',
        }}
      >
        <HeaderBar
          label={`✨ ${event.bits.toLocaleString()} BITS`}
          bg={tier.bar}
          fg={tier.fg}
          viewer={event.viewerName}
        />
        {event.message && (
          <div style={{ padding: bodyPad, wordBreak: 'break-word', fontWeight: 500 }}>
            {event.message}
          </div>
        )}
      </div>
    );
  }

  // Sub / member — single-line green bar.
  if (event.type === 'sub') {
    const tierLabel = subTierLabel(event.tier);
    const monthsTag =
      event.months > 1 ? ` · ${event.months}mo` : event.months === 1 ? ' · 1mo' : '';
    const headline = event.isGift ? `🎁 GIFT · ${tierLabel}` : `⭐ ${tierLabel}`;
    const viewer =
      event.isGift && event.gifterName
        ? `${event.gifterName} → ${event.viewerName}`
        : event.viewerName;
    return (
      <div
        style={{
          ...baseStyle,
          background: '#0E8A4F',
          color: '#FFFFFF',
          borderLeft: '3px solid #0B6B3D',
          boxShadow: '0 2px 10px rgba(14,138,79,0.3)',
          overflow: 'hidden',
        }}
      >
        <HeaderBar label={`${headline}${monthsTag}`} bg="#0B6B3D" fg="#FFFFFF" viewer={viewer} />
      </div>
    );
  }

  // Raid — single-line purple bar.
  if (event.type === 'raid') {
    return (
      <div
        style={{
          ...baseStyle,
          background: '#7C3AED',
          color: '#FFFFFF',
          borderLeft: '3px solid #5B21B6',
          boxShadow: '0 2px 10px rgba(124,58,237,0.3)',
          overflow: 'hidden',
        }}
      >
        <HeaderBar
          label={`⚔️ RAID · ${event.viewers.toLocaleString()}`}
          bg="#5B21B6"
          fg="#FFFFFF"
          viewer={event.raiderName}
        />
      </div>
    );
  }

  // Follow — subtle accent row, lighter than paid alerts.
  if (event.type === 'follow') {
    return (
      <div
        style={{
          ...baseStyle,
          background: 'rgba(255,255,255,0.04)',
          borderLeft: `3px solid ${accent}`,
          color: 'rgba(255,255,255,0.92)',
          padding: '4px 8px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
        }}
      >
        <span style={{ fontSize: fontSize * 0.95 }}>🔔</span>
        <span
          style={{
            fontWeight: 700,
            color: accent,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {event.viewerName}
        </span>
        <span style={{ opacity: 0.7, fontSize: fontSize * 0.85, flexShrink: 0 }}>followed</span>
        {ts}
      </div>
    );
  }

  return null;
}

function subTierLabel(tier: string): string {
  switch (tier) {
    case 'prime':
      return 'Prime';
    case 't1':
      return 'Tier 1';
    case 't2':
      return 'Tier 2';
    case 't3':
      return 'Tier 3';
    case 'yt-member':
      return 'YT Member';
    case 'kick-sub':
      return 'Kick Sub';
    case 'hypevault':
      return 'HypeVault';
    default:
      return tier;
  }
}
